// Iter78 — Friendly Module Scanner Forms.
//
// Shared, module-aware form surface that each scanner panel mounts
// ABOVE its existing payload textarea. The textarea moves into a
// collapsed-by-default Advanced / Developer drawer at the same time.
//
// HARD CONSTRAINTS:
//  * NO outbound HTTP traffic. No `import axios`. No `fetch(`.
//    No `XMLHttpRequest`. No `navigator.sendBeacon`. No `useEffect`.
//  * The form NEVER calls a scanner / preview / candidate / broker /
//    account / journal endpoint. It only edits a JSON string that
//    the host panel posts when the operator clicks Run on the panel.
//  * The host panel's `body` (raw JSON string state) is the single
//    source of truth. The friendly form regenerates that string on
//    every field edit. The operator can edit raw JSON in the
//    Advanced drawer; clicking "Apply friendly form to JSON" rewrites
//    the textarea from the structured fields again.

import React, { useState } from "react";
import { AlertTriangle, Wand2, RefreshCw } from "lucide-react";
import OptionChainRowsEditor from "@/components/OptionChainRowsEditor";
import SnapshotSourceStrip from "@/components/SnapshotSourceStrip";

const REGIME_CHOICES = [
  "", "SUPPORT_RECLAIM", "RESISTANCE_REJECT",
  "VALUE_COMPRESSION", "TREND_UP", "TREND_DOWN", "RANGE_BOUND",
];

// ── Module-specific shape registry ──────────────────────────────────

const MODULE_META = {
  BPCS:           { label: "BPCS",          accent: "text-[#007AFF]",  ring: "border-[#007AFF]/40", glow: "bg-[#007AFF]/10",
                    helperText: "Option chain still required before scanner run.", testidPrefix: "bpcs" },
  BCCS:           { label: "BCCS",          accent: "text-[#FF3366]",  ring: "border-[#FF3366]/40", glow: "bg-[#FF3366]/10",
                    helperText: "Option chain still required before scanner run.", testidPrefix: "bccs" },
  IRON_CONDOR:    { label: "Iron Condor",   accent: "text-[#FFB800]",  ring: "border-[#FFB800]/40", glow: "bg-[#FFB800]/10",
                    helperText: "Both put-side and call-side chains required before scanner run.", testidPrefix: "iron-condor" },
  CFM_CC_SHARES:  { label: "CFM-CC-SHARES", accent: "text-[#22D3CC]",  ring: "border-[#22D3CC]/40", glow: "bg-[#22D3CC]/10",
                    helperText: "Inventory context required for meaningful CFM-Shares scan.", testidPrefix: "cfm-shares" },
  CFM_CC_LEAPS:   { label: "CFM-CC-LEAPS",  accent: "text-[#A855F7]",  ring: "border-[#A855F7]/40", glow: "bg-[#A855F7]/10",
                    helperText: "PMCC is synthetic inventory. Assignment risk is diagnostic only.", testidPrefix: "cfm-leaps" },
};

// ── Default friendly-form values ────────────────────────────────────

function defaultForm() {
  return {
    symbols: "SPY",
    spot_price: "", val: "", vah: "", poc: "",
    wes_p: "", wes_c: "", delta_VAL: "", delta_VAH: "",
    ivr: "", regime: "",
    closed_bar_confirmed: false,
    max_symbols: "",
    // Spread / Iron Condor:
    chain_json: "",
    put_chain_json: "",
    call_chain_json: "",
    // CFM-Shares:
    shares_owned: "", share_basis: "",
    total_call_credits: "", minimum_exit_price: "",
    assignment_policy: "",
    // CFM-LEAPS:
    leaps_calls_json: "",
    short_calls_json: "",
    total_short_call_credits: "",
    allow_leaps: false,
    existing_leaps_position_json: "",
  };
}

// ── Helpers ─────────────────────────────────────────────────────────

function numOrNull(s) {
  if (s === "" || s === null || s === undefined) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function parseJsonOrEmpty(s) {
  if (!s || !s.trim()) return [];
  try { const v = JSON.parse(s); return Array.isArray(v) ? v : []; }
  catch (_e) { return []; }
}

function parseSymbols(s) {
  if (!s) return [];
  return Array.from(new Set(
    s.split(/[,\n\r\s]+/)
     .map(x => x.trim().toUpperCase())
     .filter(x => /^[A-Z][A-Z0-9.\-]*$/.test(x)),
  ));
}

function buildWes(form) {
  return {
    val: numOrNull(form.val),
    vah: numOrNull(form.vah),
    poc: numOrNull(form.poc),
    wes_p: numOrNull(form.wes_p),
    wes_c: numOrNull(form.wes_c),
    delta_VAL: numOrNull(form.delta_VAL),
    delta_VAH: numOrNull(form.delta_VAH),
    closed_bar_confirmed: !!form.closed_bar_confirmed,
  };
}

function buildPayloadFromForm(module, form) {
  const symbols = parseSymbols(form.symbols);
  const wes = buildWes(form);
  const ivr = numOrNull(form.ivr);
  const regime = form.regime || null;

  function perSymbolCommon() {
    const p = { spot_price: numOrNull(form.spot_price), wes, ivr };
    if (regime) p.regime = regime;
    return p;
  }

  const pbs = {};
  for (const sym of symbols) {
    if (module === "BPCS" || module === "BCCS") {
      pbs[sym] = { ...perSymbolCommon(), chain: parseJsonOrEmpty(form.chain_json) };
    } else if (module === "IRON_CONDOR") {
      pbs[sym] = {
        ...perSymbolCommon(),
        put_chain: parseJsonOrEmpty(form.put_chain_json),
        call_chain: parseJsonOrEmpty(form.call_chain_json),
      };
    } else if (module === "CFM_CC_SHARES") {
      pbs[sym] = {
        ...perSymbolCommon(),
        chain: parseJsonOrEmpty(form.chain_json),
        account_context: {
          shares_owned: numOrNull(form.shares_owned),
          share_basis: numOrNull(form.share_basis),
          total_call_credits: numOrNull(form.total_call_credits),
          minimum_exit_price: numOrNull(form.minimum_exit_price),
          assignment_policy: form.assignment_policy || null,
        },
      };
    } else if (module === "CFM_CC_LEAPS") {
      pbs[sym] = {
        ...perSymbolCommon(),
        option_chains: {
          leaps_calls: parseJsonOrEmpty(form.leaps_calls_json),
          short_calls: parseJsonOrEmpty(form.short_calls_json),
        },
        account_context: {
          total_short_call_credits: numOrNull(form.total_short_call_credits),
          assignment_policy: form.assignment_policy || null,
          allow_leaps: !!form.allow_leaps,
          existing_leaps_position:
            form.existing_leaps_position_json.trim()
              ? (() => { try { return JSON.parse(form.existing_leaps_position_json); }
                          catch (_e) { return null; } })()
              : null,
        },
      };
    }
  }

  const env = { watchlist: symbols, payloads_by_symbol: pbs };
  const mx = numOrNull(form.max_symbols);
  if (mx) env.max_symbols = mx;
  return env;
}

// ── Component ───────────────────────────────────────────────────────

export default function FriendlyScannerForm({ module, body, setBody, parseErr }) {
  const meta = MODULE_META[module];
  const [form, setForm] = useState(() => defaultForm());

  function setF(k, v) {
    const next = { ...form, [k]: v };
    setForm(next);
    try {
      const env = buildPayloadFromForm(module, next);
      setBody(JSON.stringify(env, null, 2));
    } catch (_e) { /* keep raw body untouched on transient error */ }
  }

  // Iter84 — applySnapshotPayload merges multiple form fields in one
  // pass so the panel body is rebuilt exactly once. The payload
  // shape comes from `SnapshotSourceStrip.extractForModule`. No
  // HTTP, no scanner run — purely form-state mutation.
  const [snapshotWarnings, setSnapshotWarnings] = useState([]);
  function applySnapshotPayload(payload) {
    if (!payload || typeof payload !== "object") return;
    const patch = {};
    if (payload.symbol)     patch.symbols = payload.symbol;
    if (payload.spot != null) patch.spot_price = String(payload.spot);
    if (payload.ivr != null)  patch.ivr = String(payload.ivr);
    if (payload.chain_json != null)
      patch.chain_json = payload.chain_json;
    if (payload.put_chain_json != null)
      patch.put_chain_json = payload.put_chain_json;
    if (payload.call_chain_json != null)
      patch.call_chain_json = payload.call_chain_json;
    if (payload.leaps_calls_json != null)
      patch.leaps_calls_json = payload.leaps_calls_json;
    if (payload.short_calls_json != null)
      patch.short_calls_json = payload.short_calls_json;
    const next = { ...form, ...patch };
    setForm(next);
    try {
      const env = buildPayloadFromForm(module, next);
      setBody(JSON.stringify(env, null, 2));
    } catch (_e) {
      /* keep raw body untouched on transient error */
    }
    setSnapshotWarnings(Array.isArray(payload.warnings) ? payload.warnings : []);
  }

  function syncFromJson() {
    try {
      const parsed = JSON.parse(body || "{}");
      const watchlist = Array.isArray(parsed.watchlist) ? parsed.watchlist : [];
      const firstSym = watchlist[0];
      const ps = (parsed.payloads_by_symbol || {})[firstSym] || {};
      const wes = ps.wes || {};
      setForm({
        ...defaultForm(),
        symbols: watchlist.join(", "),
        spot_price: ps.spot_price != null ? String(ps.spot_price) : "",
        val: wes.val != null ? String(wes.val) : "",
        vah: wes.vah != null ? String(wes.vah) : "",
        poc: wes.poc != null ? String(wes.poc) : "",
        wes_p: wes.wes_p != null ? String(wes.wes_p) : "",
        wes_c: wes.wes_c != null ? String(wes.wes_c) : "",
        delta_VAL: wes.delta_VAL != null ? String(wes.delta_VAL) : "",
        delta_VAH: wes.delta_VAH != null ? String(wes.delta_VAH) : "",
        closed_bar_confirmed: !!wes.closed_bar_confirmed,
        ivr: ps.ivr != null ? String(ps.ivr) : "",
        regime: ps.regime || "",
        max_symbols: parsed.max_symbols != null ? String(parsed.max_symbols) : "",
        chain_json: Array.isArray(ps.chain) ? JSON.stringify(ps.chain, null, 2) : "",
        put_chain_json: Array.isArray(ps.put_chain) ? JSON.stringify(ps.put_chain, null, 2) : "",
        call_chain_json: Array.isArray(ps.call_chain) ? JSON.stringify(ps.call_chain, null, 2) : "",
        leaps_calls_json: ps.option_chains?.leaps_calls ? JSON.stringify(ps.option_chains.leaps_calls, null, 2) : "",
        short_calls_json: ps.option_chains?.short_calls ? JSON.stringify(ps.option_chains.short_calls, null, 2) : "",
        shares_owned: ps.account_context?.shares_owned != null ? String(ps.account_context.shares_owned) : "",
        share_basis: ps.account_context?.share_basis != null ? String(ps.account_context.share_basis) : "",
        total_call_credits: ps.account_context?.total_call_credits != null ? String(ps.account_context.total_call_credits) : "",
        minimum_exit_price: ps.account_context?.minimum_exit_price != null ? String(ps.account_context.minimum_exit_price) : "",
        assignment_policy: ps.account_context?.assignment_policy || "",
        total_short_call_credits: ps.account_context?.total_short_call_credits != null ? String(ps.account_context.total_short_call_credits) : "",
        allow_leaps: !!ps.account_context?.allow_leaps,
        existing_leaps_position_json: ps.account_context?.existing_leaps_position
          ? JSON.stringify(ps.account_context.existing_leaps_position, null, 2)
          : "",
      });
    } catch (_e) {
      // Silently ignore; the advanced textarea already shows parse errors.
    }
  }

  const tid = meta.testidPrefix;

  return (
    <div
      data-testid={`${tid}-scanner-friendly-form`}
      className={`rounded-sm border ${meta.ring} ${meta.glow} p-3 space-y-3`}
    >
      <div className="flex items-center gap-2">
        <Wand2 size={12} className={meta.accent} />
        <span className={`text-[10px] font-mono uppercase tracking-[0.14em] ${meta.accent}`}>
          Friendly {meta.label} input · default mode
        </span>
        <button
          type="button"
          onClick={syncFromJson}
          data-testid={`${tid}-scanner-friendly-sync`}
          aria-label="Repopulate friendly form from current JSON body"
          className="ml-auto px-2 py-0.5 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
        >
          <RefreshCw size={10} />
          Sync from JSON
        </button>
      </div>

      {/* Iter84 — Snapshot source strip. Only renders when an
          in-memory snapshot exists in MarketSnapshotContext.
          Operator must explicitly click Use Fresh Snapshot. */}
      <SnapshotSourceStrip
        module={module}
        testidPrefix={`${tid}-scanner-snapshot-strip`}
        onApply={applySnapshotPayload}
      />
      {snapshotWarnings.length > 0 && (
        <div
          data-testid={`${tid}-scanner-snapshot-strip-applied-warnings`}
          className="rounded-sm border border-[#FFB800]/30 bg-[#FFB800]/5 p-2 text-[10px] font-mono text-[#FFB800] space-y-0.5"
        >
          {snapshotWarnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1">
              <AlertTriangle size={10} className="shrink-0 mt-0.5" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* Common context */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px] font-mono">
        <Field label="Symbols" value={form.symbols} onChange={v => setF("symbols", v)} testid={`${tid}-scanner-friendly-symbols`} multiline />
        <Field label="Spot"    value={form.spot_price} onChange={v => setF("spot_price", v)} testid={`${tid}-scanner-friendly-spot`} />
        <Field label="IVR"     value={form.ivr} onChange={v => setF("ivr", v)} testid={`${tid}-scanner-friendly-ivr`} />
        <Field label="Max symbols" value={form.max_symbols} onChange={v => setF("max_symbols", v)} testid={`${tid}-scanner-friendly-max-symbols`} />
        <Field label="VAL"   value={form.val} onChange={v => setF("val", v)} testid={`${tid}-scanner-friendly-val`} />
        <Field label="POC"   value={form.poc} onChange={v => setF("poc", v)} testid={`${tid}-scanner-friendly-poc`} />
        <Field label="VAH"   value={form.vah} onChange={v => setF("vah", v)} testid={`${tid}-scanner-friendly-vah`} />
        <SelectField label="Regime hint" value={form.regime} onChange={v => setF("regime", v)}
                      options={REGIME_CHOICES} testid={`${tid}-scanner-friendly-regime`} />
        <Field label="WES-P" value={form.wes_p} onChange={v => setF("wes_p", v)} testid={`${tid}-scanner-friendly-wes-p`} />
        <Field label="WES-C" value={form.wes_c} onChange={v => setF("wes_c", v)} testid={`${tid}-scanner-friendly-wes-c`} />
        <Field label="Δ VAL" value={form.delta_VAL} onChange={v => setF("delta_VAL", v)} testid={`${tid}-scanner-friendly-delta-val`} />
        <Field label="Δ VAH" value={form.delta_VAH} onChange={v => setF("delta_VAH", v)} testid={`${tid}-scanner-friendly-delta-vah`} />
        <label className="flex items-center gap-2 col-span-2">
          <input
            type="checkbox"
            data-testid={`${tid}-scanner-friendly-bar-closed`}
            checked={form.closed_bar_confirmed}
            onChange={e => setF("closed_bar_confirmed", e.target.checked)}
          />
          <span className="text-[#8A8A93] uppercase tracking-[0.12em]">closed bar confirmed</span>
        </label>
      </div>

      {/* Module-specific blocks */}
      {(module === "BPCS" || module === "BCCS" || module === "CFM_CC_SHARES") && (
        <ChainEditor
          label={module === "CFM_CC_SHARES" ? "Call chain (JSON array)" : "Option chain (JSON array)"}
          value={form.chain_json}
          onChange={v => setF("chain_json", v)}
          kind={module === "BPCS" ? "puts" : "calls"}
          testid={`${tid}-scanner-friendly-chain`} />
      )}

      {module === "IRON_CONDOR" && (
        <>
          <ChainEditor label="Put-side chain (JSON array)"
            value={form.put_chain_json} onChange={v => setF("put_chain_json", v)}
            kind="puts"
            testid={`${tid}-scanner-friendly-put-chain`} />
          <ChainEditor label="Call-side chain (JSON array)"
            value={form.call_chain_json} onChange={v => setF("call_chain_json", v)}
            kind="calls"
            testid={`${tid}-scanner-friendly-call-chain`} />
        </>
      )}

      {module === "CFM_CC_SHARES" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px] font-mono">
          <Field label="Shares owned"          value={form.shares_owned} onChange={v => setF("shares_owned", v)} testid={`${tid}-scanner-friendly-shares-owned`} />
          <Field label="Share basis"           value={form.share_basis} onChange={v => setF("share_basis", v)} testid={`${tid}-scanner-friendly-share-basis`} />
          <Field label="Total call credits"    value={form.total_call_credits} onChange={v => setF("total_call_credits", v)} testid={`${tid}-scanner-friendly-total-call-credits`} />
          <Field label="Min exit price"        value={form.minimum_exit_price} onChange={v => setF("minimum_exit_price", v)} testid={`${tid}-scanner-friendly-min-exit-price`} />
          <Field label="Assignment policy"     value={form.assignment_policy} onChange={v => setF("assignment_policy", v)} testid={`${tid}-scanner-friendly-assignment-policy`} multiline={false} />
        </div>
      )}

      {module === "CFM_CC_LEAPS" && (
        <>
          <ChainEditor label="LEAPS calls (JSON array)"
            value={form.leaps_calls_json} onChange={v => setF("leaps_calls_json", v)}
            kind="calls"
            testid={`${tid}-scanner-friendly-leaps-calls`} />
          <ChainEditor label="Short calls (JSON array)"
            value={form.short_calls_json} onChange={v => setF("short_calls_json", v)}
            kind="calls"
            testid={`${tid}-scanner-friendly-short-calls`} />
          <ChainEditor label="Existing LEAPS position (optional JSON object)"
            value={form.existing_leaps_position_json}
            onChange={v => setF("existing_leaps_position_json", v)}
            testid={`${tid}-scanner-friendly-existing-leaps-position`} />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[10px] font-mono">
            <Field label="Total short-call credits"
              value={form.total_short_call_credits}
              onChange={v => setF("total_short_call_credits", v)}
              testid={`${tid}-scanner-friendly-total-short-call-credits`} />
            <Field label="Assignment policy"
              value={form.assignment_policy}
              onChange={v => setF("assignment_policy", v)}
              testid={`${tid}-scanner-friendly-assignment-policy`} />
            <label className="flex items-center gap-2 mt-4">
              <input
                type="checkbox"
                data-testid={`${tid}-scanner-friendly-allow-leaps`}
                checked={form.allow_leaps}
                onChange={e => setF("allow_leaps", e.target.checked)}
              />
              <span className="text-[#8A8A93] uppercase tracking-[0.12em]">allow LEAPS</span>
            </label>
          </div>
          <div
            data-testid={`${tid}-scanner-friendly-automation-disclaimer`}
            className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#FFB800] flex flex-wrap gap-2"
          >
            <span>No LEAPS exercise automation</span>
            <span>· No assignment automation</span>
          </div>
        </>
      )}

      {/* Helper text + parse error mirror */}
      <div
        data-testid={`${tid}-scanner-friendly-helper`}
        className="text-[10px] font-mono text-[#8A8A93] flex items-start gap-1"
      >
        <AlertTriangle size={11} className="mt-0.5 shrink-0 text-[#FFB800]" />
        <span>{meta.helperText}</span>
      </div>
      {parseErr && (
        <div
          data-testid={`${tid}-scanner-friendly-parse-error-mirror`}
          className="text-[10px] font-mono text-[#FF3366]"
        >
          {parseErr}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, multiline }) {
  return (
    <label className="flex flex-col">
      <span className="text-[#5F5F66] uppercase tracking-[0.12em] mb-1">{label}</span>
      {multiline
        ? <textarea
            data-testid={testid}
            value={value}
            onChange={e => onChange(e.target.value)}
            rows={2}
            spellCheck={false}
            className="bg-[#08080A] border border-[#26262B] rounded-sm px-1 py-0.5 text-[#E0E0E0] focus:outline-none" />
        : <input
            type="text"
            data-testid={testid}
            value={value}
            onChange={e => onChange(e.target.value)}
            className="bg-[#08080A] border border-[#26262B] rounded-sm px-1 py-0.5 text-[#E0E0E0] focus:outline-none" />
      }
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="flex flex-col">
      <span className="text-[#5F5F66] uppercase tracking-[0.12em] mb-1">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="bg-[#08080A] border border-[#26262B] rounded-sm px-1 py-0.5 text-[#E0E0E0]"
      >
        {options.map(opt => (
          <option key={opt || "_blank"} value={opt}>{opt || "—"}</option>
        ))}
      </select>
    </label>
  );
}

function ChainEditor({ label, value, onChange, testid, kind }) {
  return (
    <label className="block">
      <span className="text-[#5F5F66] uppercase tracking-[0.12em] mb-1 text-[10px] font-mono">{label}</span>
      {/* Iter81 — friendly row-based table editor sits ABOVE the
          existing raw JSON mini-editor. Both round-trip through the
          same string state, so the JSON shape the scanner endpoint
          expects is preserved exactly. The JSON textarea remains
          available via the editor's "Advanced JSON" mode toggle. */}
      <OptionChainRowsEditor
        value={value}
        onChange={onChange}
        testid={testid}
        kind={kind}
      />
      <details
        data-testid={`${testid}-mini-json-fallback`}
        className="mt-1"
      >
        <summary className="cursor-pointer text-[9px] font-mono uppercase tracking-[0.14em] text-[#5F5F66] hover:text-[#E0E0E0]">
          Advanced — paste raw JSON
        </summary>
        <textarea
          data-testid={testid}
          value={value}
          onChange={e => onChange(e.target.value)}
          rows={3}
          spellCheck={false}
          placeholder='[ { "type":"put", "strike":490, "bid":1.6, "ask":1.65, "delta":-0.25, "open_interest":1500, "volume":100, "expiration":"2026-06-21", "dte":21 } ]'
          className="w-full mt-1 bg-[#08080A] border border-[#26262B] rounded-sm px-1 py-0.5 text-[10px] font-mono text-[#E0E0E0] focus:outline-none"
        />
      </details>
    </label>
  );
}
